import { useEffect, useId, useState } from 'react';
import { LoginIdSchema, PasswordSchema, CodeSchema, EnrollmentInputSchema } from '@mcpanel/core';
import { SuccessCheck, useShake } from './feedback';

const hints = {
  login: '英小文字・数字・_・- の 3〜32 文字（大文字は自動で小文字になります）',
  password: '12〜128 文字。ログイン ID をそのまま含めることはできません',
};

export function AuthField({
  label,
  value,
  onChange,
  password = false,
  login = false,
  code = false,
  newPassword = false,
  disabled = false,
  totpOnly = false,
  recoveryOnly = false,
  autoFocus = false,
  loginValue,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  password?: boolean;
  login?: boolean;
  code?: boolean;
  newPassword?: boolean;
  disabled?: boolean;
  totpOnly?: boolean;
  recoveryOnly?: boolean;
  autoFocus?: boolean;
  loginValue?: string;
}) {
  const hintId = useId();
  const inputId = useId();
  const errorId = useId();
  const [touched, setTouched] = useState(false);
  const shake = useShake();
  const parsed = login
    ? LoginIdSchema.safeParse(value)
    : password
      ? PasswordSchema.safeParse(value)
      : code
        ? CodeSchema.safeParse(value)
        : EnrollmentInputSchema.shape.name.safeParse(value);
  let problem =
    parsed && !parsed.success
      ? code
        ? 'ワンタイムコード（6 桁）またはリカバリーコードを入力してください'
        : !login && !password
          ? '表示名は 1〜100 文字にしてください'
          : parsed.error.issues[0]?.message
      : undefined;
  if (code && totpOnly && !/^[0-9]{6}$/.test(value)) problem = 'ワンタイムコード（6 桁）を入力してください';
  if (code && recoveryOnly && !/^[A-Z2-7]{10}$/.test(value)) problem = 'リカバリーコード（10 文字）を入力してください';
  if (newPassword && loginValue) {
    const enrollment = EnrollmentInputSchema.safeParse({ name: '確認', login: loginValue, password: value });
    if (!enrollment.success)
      problem = enrollment.error.issues.find((issue) => issue.path[0] === 'password')?.message ?? problem;
  }
  if (!value) problem = '入力してください';
  const error = touched ? problem : undefined;
  const valid = !!value && !problem;
  useEffect(() => {
    if (error) shake.trigger();
  }, [error, shake.trigger]);
  const hint = login ? hints.login : password && newPassword ? hints.password : undefined;
  return (
    <>
      <div
        ref={shake.ref}
        className={`field auth-floating${value ? ' filled' : ''}${valid ? ' valid' : error ? ' invalid' : ''}`}
      >
        <label htmlFor={inputId}>{label}</label>
        <input
          id={inputId}
          className="input"
          onBlur={() => setTouched(true)}
          onInvalid={() => {
            setTouched(true);
            shake.trigger();
          }}
          aria-invalid={error ? true : undefined}
          type={password ? 'password' : 'text'}
          value={value}
          autoFocus={autoFocus}
          // スマホのキーボードは先頭を大文字にするので、ログイン ID は小文字にそろえる。
          onChange={(e) =>
            onChange(code ? e.target.value.toUpperCase() : login ? e.target.value.toLowerCase() : e.target.value)
          }
          autoCapitalize={login || code ? 'none' : undefined}
          autoCorrect={login || code ? 'off' : undefined}
          spellCheck={login || code ? false : undefined}
          title={hint}
          aria-describedby={
            [hint ? hintId : undefined, error ? errorId : undefined].filter(Boolean).join(' ') || undefined
          }
          autoComplete={
            password
              ? newPassword
                ? 'new-password'
                : 'current-password'
              : code
                ? 'one-time-code'
                : login
                  ? 'username'
                  : 'name'
          }
          minLength={password ? 12 : login ? 3 : undefined}
          maxLength={password ? 128 : login ? 32 : code ? (totpOnly ? 6 : 10) : 100}
          inputMode={totpOnly ? 'numeric' : undefined}
          pattern={
            login
              ? '[a-z0-9_-]{3,32}'
              : code
                ? totpOnly
                  ? '[0-9]{6}'
                  : recoveryOnly
                    ? '[A-Z2-7]{10}'
                    : '(?:[0-9]{6}|[A-Z2-7]{10})'
                : undefined
          }
          required
          disabled={disabled}
        />
        {valid && <SuccessCheck />}
      </div>
      {error && (
        <small id={errorId} role="alert" className="auth-error" style={{ color: 'var(--danger)' }}>
          {error}
        </small>
      )}
      {hint && (
        <small id={hintId} className="muted">
          {hint}
        </small>
      )}
    </>
  );
}
